import { useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { config } from '../config/env';

import Layout from "../components/layout/Layout";
import Select from "../components/select/Select";
import InputText from "../components/inputText/InputText";
import Button from "../components/button/Button";
import ContentList from "../components/contentList/ContentList";
import Pagination from "../components/pagination/Pagination";
import EmptyContent from "../components/emptycontent/EmptyContent";
import ContentListLoader from "../components/contentListLoader/ContentListLoader";

const fetchTopics = async () => {
  try {
    const response = await fetch(`${config.apiBaseUrl}/api/v1/topics/`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching topics:', error);
    throw error;
  }
}

const fetchResearchPapers = async ({
  page=1, 
  limit=5, 
  sort_By="published_at", 
  order_by="desc", 
  sas_token_for_first,
  search,
  topic_id
}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    params.append('sort_by', sort_By);
    params.append('order_by', order_by);
    if(sas_token_for_first){
      params.append('sas_token_for_first', sas_token_for_first);
    }
    if (search) {
      params.append('search', search);
    }
    if(topic_id) {
      params.append('topic_id', topic_id);
    }
    const response = await fetch(`${config.apiBaseUrl}/api/v1/content/research_papers/?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching research papers:', error);
    throw error;
  }
}

const PublicResearchPapers = () => {
  // DEFAULT STATE
  const topicsDefault = [
    { id: 0, name: "All" },
  ]
  const filterDefault = {
    selectedTopic: topicsDefault[0],
    search: "",
    currentPage: 1,
    itemsPerPage: 5,
  }
  const inputDefault = {
    search: ""
  }

  // HOOKS
  const [input, setInput] = useState(inputDefault);
  const [filter, setFilter] = useState(filterDefault);
  const [topics, setTopics] = useState(topicsDefault);
  const [researchPapers, setResearchPapers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true);

        const fetchedTopics = await fetchTopics();
        setTopics([topicsDefault[0], ...fetchedTopics.data]);
        
        const fetchedResearchPapers = await fetchResearchPapers({ limit: filter.itemsPerPage });
        setResearchPapers(fetchedResearchPapers);

        setIsLoading(false);
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };

    initialize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const updateResearchPapers = async () => {
      try {
        setIsLoading(true);

        const fetchedResearchPapers = await fetchResearchPapers({ 
          page: filter.currentPage, 
          limit: filter.itemsPerPage,
          search: filter.search?.id !== 0 ? filter.search : undefined,
          topic_id: filter.selectedTopic?.id !== 0 ? filter.selectedTopic?.id : undefined 
        });
        setResearchPapers(fetchedResearchPapers);
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error updating research papers:", error);
      }
    };

    updateResearchPapers();
  }, [filter])

  // EVENT HANDLERS
  const handleTopicChange = (selectedTopic) => {
    setFilter({ ...filter, selectedTopic: selectedTopic, currentPage: 1 });
  }

  const handleSearch = () => {
    setFilter({ ...filter, search: input.search, currentPage: 1 });
  }

  const handleResetFilter = () => {
    setInput(inputDefault)
    setFilter(filterDefault)
  }

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, currentPage: newPage });
  }

  // RENDER
  const renderList = () => {
    if (isLoading) {
      return <ContentListLoader />
    }
    if (researchPapers.count === 0) {
      return <EmptyContent />;
    } else {
      return (
        <ContentList contents={researchPapers} isAdmin={false} showContentStatus={false}/>
      )
    }
  }

  return (
    <div>
        <Helmet>
            <title>Avanade Research</title>
        </Helmet>
        <Layout>
          <div className="text-sm mb-10">
            <div className="flex w-full my-10">
              <div className="block w-full">
                <div className="text-lg font-semibold">Research Papers</div>
                <div className="text-gray-600">
                  Articles written by researchers to present the results of studies and experiments.
                </div>
              </div>
            </div>
            <div className="flex items-end gap-5 mb-10">
              <div className="w-64">
                <Select
                  items={topics}
                  label="Topic"
                  itemChangeHandler={handleTopicChange}
                  selectedItemId={filter.selectedTopic?.id} 
                />
              </div>
              <div className="w-96">
                <InputText
                  label="Search"
                  value={input.search}
                  onBlur={(e) => setInput({ ...input, search: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  onChange={(e) => setInput({ ...input, search: e.target.value })}
                />
              </div>
              <div className="flex gap-3">
                <Button buttonClassName="py-2 px-5" onClick={handleSearch}>
                  Search
                </Button>
                <Button buttonClassName="py-2 px-5" onClick={handleResetFilter}>
                  Clear
                </Button>
              </div>
            </div>
            <div className="min-h-[580px]">
              { renderList() }
            </div>
            <Pagination
              currentPage={filter.currentPage}
              totalItems={researchPapers.count}
              itemsPerPage={filter.itemsPerPage}
              setCurrentPage={handlePageChange}
            />
          </div>
        </Layout>
    </div>
  );
};

export default PublicResearchPapers;
